import React from "react";
import { useTranslation } from "react-i18next";
import { AiEngineList, AiParams, MenuList } from "../types/junoTypes";

export type ImageModel = {
  value: AiEngineList;
  label: string;
  description: string;
  category: string[];
};

export type ImageModels = {
  txt2img: ImageModel[];
  img2img: ImageModel[];
  editor: ImageModel[];
  upscale: ImageModel[];
  context: ImageModel[];
};

export const useJunoModels = () => {
  const { t } = useTranslation();

  const getModelParams = (aiEngine: AiEngineList, menu: MenuList) => {
    let params: AiParams[] = [];
    switch (aiEngine) {
      case "DallE3":
        params.push("prompt", "height", "width", "aspectRatio", "style", "revisedPrompt");
        break;
      case "GPTImage1":
        params.push("sample", "prompt", "height", "width", "aspectRatio", "quality");
        break;
      case "Ghiblify":
        // Imageのみ必要
        break;
      case "SanaSprint":
        params.push("prompt", "negativePrompt", "aspectRatio", "guidanceScale", "sample", "style", "seed"); //"step",
        break;
      case "Sana48B":
      case "InstantCharacter":
        params.push(
          "prompt",
          "negativePrompt",
          "aspectRatio",
          "step",
          "guidanceScale",
          "sample",
          "styleWeight",
          "style",
          "seed"
        );
        break;
      case "SD35":
      case "SDUltra":
        params.push("prompt", "aspectRatio", "negativePrompt", "seed");
        break;
      case "Flux11Pro":
        params.push("sample", "prompt", "aspectRatio", "seed");
        break;
      case "Flux11ProRedux":
        params.push("prompt", "aspectRatio", "seed");
        break;
      case "Flux11ProUltra":
        params.push("sample", "prompt", "aspectRatio", "rawMode", "seed");
        break;
      case "Flux11ProUltraRedux":
        params.push("prompt", "aspectRatio", "rawMode", "seed");
        break;
      case "Flux1ProCanny":
      case "Flux1ProDepth":
        params.push("prompt", "aspectRatio", "seed", "step", "guidanceScale");
        break;
      case "Flux1ProCannyJuno":
      case "Flux1ProDepthJuno":
        params.push("prompt", "aspectRatio", "loras", "seed", "step", "guidanceScale");
        break;
      case "Flux1ProJuno":
        params.push("sample", "prompt", "aspectRatio", "loras", "guidanceScale", "seed");
        break;
      case "Flux11ProUltraJuno":
        params.push("sample", "prompt", "aspectRatio", "loras", "rawMode", "seed");
        break;
      case "Flux1Dev":
        params.push("sample", "aspectRatio", "seed", "step", "guidanceScale");
        break;
      case "Flux1DevRedux": // Samplesが動かないので一時的に削除
      case "Flux1SchnellRedux":
        params.push("sample", "aspectRatio", "seed", "step", "guidanceScale");
        break;
      case "Flux1Schnell":
        params.push("prompt", "sample", "aspectRatio", "seed", "step", "guidanceScale");
        break;
      case "FluxPuLID":
        params.push("aspectRatio", "seed", "step", "guidanceScale", "negativePrompt");
        break;
      case "Flux1RealismLoRA":
        params.push("prompt", "aspectRatio", "seed", "step", "guidanceScale");
        break;
      case "FluxJuno":
        params.push("sample", "prompt", "aspectRatio", "loras", "seed", "step", "guidanceScale");
        break;
      case "Flux1DevCannyLoRA":
      case "Flux1DevDepthLoRA":
        params.push("sample", "prompt", "aspectRatio", "loras", "seed", "step", "guidanceScale");
        break;
      case "Flux1ProFill":
        params.push("prompt", "editor", "seed");
        break;
      case "Flux1ProFillJuno":
        params.push("prompt", "editor", "loras", "seed");
        break;
      case "Flux1DevFill":
        params.push("prompt", "editor", "loras", "seed", "step", "sample");
        break;
      case "GeminiFlashEdit":
        params.push("prompt");
        break;
      case "KolorsVTO":
        params.push("prompt");
        break;
      case "BriaBackgroundReplace":
        params.push("prompt", "negativePrompt", "seed", "sample");
        break;
      case "BriaProductShot":
        params.push("prompt", "sample", "aspectRatio", "manualPlacementSelection");
        break;
      case "IcLightV2":
        params.push("sample", "prompt", "aspectRatio", "seed", "step", "guidanceScale", "initialLatent");
        break;
      case "LuminaV2":
        params.push("sample", "prompt", "systemPrompt", "aspectRatio", "seed", "step", "guidanceScale");
        break;
      case "Imagen3":
      case "Imagen4":
        params.push("sample", "prompt", "negativePrompt", "aspectRatio", "seed", "quality");
        break;
      case "RecraftV3":
        params.push("prompt", "aspectRatio", "style", "colors");
        break;
      case "IdeogramV3":
        params.push(
          "prompt",
          "aspectRatio",
          "revisedPrompt",
          "seed",
          "quality",
          "sample",
          "style",
          "negativePrompt",
          "colors"
        );
        break;
      case "IdeogramV3Remix":
        params.push(
          "prompt",
          "aspectRatio",
          "revisedPrompt",
          "seed",
          "quality",
          "sample",
          "style",
          "negativePrompt",
          "colors"
        );
        break;
      case "IdeogramV3Edit":
        params.push(
          "prompt",
          "editor",
          "revisedPrompt",
          "seed",
          "quality",
          "sample",
          "style",
          "negativePrompt",
          "colors"
        );
        break;
      case "IdeogramV3ReplaceBackground":
        params.push(
          "prompt",
          "aspectRatio",
          "revisedPrompt",
          "seed",
          "quality",
          "sample",
          "style",
          "negativePrompt",
          "colors"
        );
        break;
      case "MinimaxImage":
        params.push("prompt", "aspectRatio", "sample", "revisedPrompt");
        break;
      case "FluxJuggernautBase":
      case "FluxJuggernautPro":
        params.push("sample", "prompt", "aspectRatio", "seed", "step", "guidanceScale");
        break;
      case "FluxJuggernautLightning":
        params.push("prompt", "sample", "aspectRatio", "seed", "step");
        break;
      case "FluxJuggernautJuno":
        params.push("sample", "prompt", "aspectRatio", "loras", "seed", "step", "guidanceScale");
        break;
      case "FluxProKontextMax":
      case "FluxProKontext":
        params.push("sample", "prompt", "aspectRatio", "seed", "guidanceScale");
        break;
      default:
        break;
    }

    // GPTImage1は常に画像パラメータを表示
    if (aiEngine === "GPTImage1") {
      params.push("initImage", "initImage2", "initImage3", "initImage4");
    }

    // contextメニューまたはI2Iの場合は画像パラメータを追加
    
    if (["IdeogramV3", "IdeogramV3Remix", "IdeogramV3Edit", "IdeogramV3ReplaceBackground"].includes(aiEngine)) {
      params.push("initImage2");
    }

    if (["img2img", "editor", "context"].includes(menu)) {
      if (["GeminiFlashEdit", "FluxProKontextMax", "FluxProKontext"].includes(aiEngine)) {
        params.push("initImage", "initImage2", "initImage3", "initImage4");
      } else if (
        [
          "SD35",
          "FluxJuno",
          "FluxPuLID",
          "Flux1Dev",
          "Flux11ProUltra",
          "Flux11ProRedux",
          "Flux11ProUltraRedux",
          "Flux1ProCanny",
          "Flux1ProDepth",
          "Flux1DevRedux",
          "Flux1DevCannyLoRA",
          "Flux1DevDepthLoRA",
          "Flux1SchnellRedux",
          "Flux1ProCannyJuno",
          "Flux1ProDepthJuno",
          "KolorsVTO",
          "IdeogramV3Remix",
          "IdeogramV3ReplaceBackground",
          "BriaBackgroundReplace",
          "BriaProductShot",
          "IcLightV2",
          "GeminiFlashEdit",
          "Ghiblify",
          "InstantCharacter",
          "RecraftV3",
        ].includes(aiEngine)
      ) {
        params.push("initImage");
      }

      if (["KolorsVTO", "BriaBackgroundReplace", "BriaProductShot"].includes(aiEngine)) {
        params.push("initImage2");
      }

      // I2Iの場合は画像強度パラメータを追加
      if (
        [
          "SD35",
          "FluxJuno",
          "Flux1Dev",
          "FluxPuLID",
          "Flux11ProUltra",
          "Flux11ProUltraRedux",
          "IdeogramV3Remix",
          "RecraftV3",
        ].includes(aiEngine)
      ) {
        params.push("imageStrength");
      }

      // I2Iの場合に特定のパラメータ（例えばアスペクト比）があれば削除
      if (["Flux1Dev"].includes(aiEngine)) {
        params = params.filter((param) => param !== "aspectRatio");
      }
    }
    return params;
  };

  const getImageModels = React.useMemo<ImageModels>(() => {
    return {
      txt2img: [
        {
          value: "DallE3",
          label: t("juno.input.engine.DallE3"),
          description: t("juno.input.engine.DallE3Description"),
          category: ["pro"],
        },

        {
          value: "Flux11ProUltra",
          label: t("juno.input.engine.Flux11ProUltra"),
          description: t("juno.input.engine.Flux11ProUltraDescription"),
          category: ["pro", "2k"],
        },
        {
          value: "Flux11ProUltraJuno",
          label: t("juno.input.engine.Flux11ProUltraJuno"),
          description: t("juno.input.engine.Flux11ProUltraJunoDescription"),
          category: ["juno", "2k", "pro"],
        },
        {
          value: "Flux11Pro",
          label: t("juno.input.engine.Flux11Pro"),
          description: t("juno.input.engine.Flux1ProDescription"),
          category: [],
        },
        {
          value: "Flux1ProJuno",
          label: t("juno.input.engine.Flux1ProJuno"),
          description: t("juno.input.engine.Flux1ProJunoDescription"),
          category: ["juno"],
        },
        {
          value: "Flux1Dev",
          label: t("juno.input.engine.Flux1Dev"),
          description: t("juno.input.engine.Flux1DevDescription"),
          category: [],
        },
        {
          value: "FluxJuno",
          label: t("juno.input.engine.FluxJuno"),
          description: t("juno.input.engine.FluxJunoDescription"),
          category: ["juno"],
        },
        {
          value: "Flux1RealismLoRA",
          label: t("juno.input.engine.Flux1RealismLoRA"),
          description: t("juno.input.engine.Flux1RealismLoRADescription"),
          category: [],
        },
        {
          value: "Flux1Schnell",
          label: t("juno.input.engine.Flux1Schnell"),
          description: t("juno.input.engine.Flux1SchnellDescription"),
          category: [],
        },
        {
          value: "FluxJuggernautLightning",
          label: t("juno.input.engine.FluxJuggernautLightning"),
          description: t("juno.input.engine.FluxJuggernautLightningDescription"),
          category: [],
        },
        {
          value: "FluxJuggernautBase",
          label: t("juno.input.engine.FluxJuggernautBase"),
          description: t("juno.input.engine.FluxJuggernautBaseDescription"),
          category: [],
        },
        {
          value: "FluxJuggernautPro",
          label: t("juno.input.engine.FluxJuggernautPro"),
          description: t("juno.input.engine.FluxJuggernautProDescription"),
          category: ["pro"],
        },
        {
          value: "FluxJuggernautJuno",
          label: t("juno.input.engine.FluxJuggernautJuno"),
          description: t("juno.input.engine.FluxJuggernautJunoDescription"),
          category: ["juno"],
        },
        {
          value: "IdeogramV3",
          label: t("juno.input.engine.IdeogramV3"),
          description: t("juno.input.engine.IdeogramV3Description"),
          category: [],
        },
        {
          value: "Imagen4",
          label: t("juno.input.engine.Imagen4"),
          description: t("juno.input.engine.Imagen4Description"),
          category: [],
        },
        {
          value: "Imagen3",
          label: t("juno.input.engine.Imagen3"),
          description: t("juno.input.engine.Imagen3Description"),
          category: [],
        },
        {
          value: "LuminaV2",
          label: t("juno.input.engine.LuminaV2"),
          description: t("juno.input.engine.LuminaV2Description"),
          category: [],
        },
        {
          value: "MinimaxImage",
          label: t("juno.input.engine.MinimaxImage"),
          description: t("juno.input.engine.MinimaxImageDescription"),
          category: [],
        },
        {
          value: "RecraftV3",
          label: t("juno.input.engine.RecraftV3"),
          description: t("juno.input.engine.RecraftV3Description"),
          category: ["vector"],
        },
        {
          value: "Sana48B",
          label: t("juno.input.engine.Sana48B"),
          description: t("juno.input.engine.Sana48BDescription"),
          category: ["4k"],
        },
        {
          value: "SanaSprint",
          label: t("juno.input.engine.SanaSprint"),
          description: t("juno.input.engine.SanaSprintDescription"),
          category: ["4k"],
        },
        {
          value: "SD35",
          label: t("juno.input.engine.SD35"),
          description: t("juno.input.engine.SD35Description"),
          category: [],
        },
        {
          value: "SDUltra",
          label: t("juno.input.engine.SDUltra"),
          description: t("juno.input.engine.SDUltraDescription"),
          category: [],
        },
      ],
      img2img: [
        {
          value: "Flux11ProUltraRedux",
          label: t("juno.input.engine.Flux11ProUltraRedux"),
          description: t("juno.input.engine.Flux11ProUltraReduxDescription"),
          category: ["pro", "vary", "2k"],
        },
        {
          value: "Flux11ProRedux",
          label: t("juno.input.engine.Flux11ProRedux"),
          description: t("juno.input.engine.Flux11ProReduxDescription"),
          category: ["vary"],
        },
        {
          value: "Flux1ProCanny",
          label: t("juno.input.engine.Flux1ProCanny"),
          description: t("juno.input.engine.Flux1ProCannyDescription"),
          category: ["pro", "controlNet"],
        },
        {
          value: "Flux1ProCannyJuno",
          label: t("juno.input.engine.Flux1ProCannyJuno"),
          description: t("juno.input.engine.Flux1ProCannyJunoDescription"),
          category: ["juno", "pro", "controlNet"],
        },
        {
          value: "Flux1ProDepth",
          label: t("juno.input.engine.Flux1ProDepth"),
          description: t("juno.input.engine.Flux1ProDepthDescription"),
          category: ["pro", "controlNet"],
        },
        {
          value: "Flux1ProDepthJuno",
          label: t("juno.input.engine.Flux1ProDepthJuno"),
          description: t("juno.input.engine.Flux1ProDepthJunoDescription"),
          category: ["juno", "pro", "controlNet"],
        },
        {
          value: "Flux1Dev",
          label: t("juno.input.engine.Flux1Dev"),
          description: t("juno.input.engine.Flux1DevDescription"),
          category: [],
        },
        {
          value: "Flux1DevRedux",
          label: t("juno.input.engine.Flux1DevRedux"),
          description: t("juno.input.engine.Flux1DevReduxDescription"),
          category: ["vary"],
        },
        {
          value: "FluxJuno",
          label: t("juno.input.engine.FluxJuno"),
          description: t("juno.input.engine.FluxJunoDescription"),
          category: [],
        },
        {
          value: "Flux1DevCannyLoRA",
          label: t("juno.input.engine.Flux1DevCannyLoRA"),
          description: t("juno.input.engine.Flux1DevCannyLoRADescription"),
          category: ["controlNet"],
        },
        {
          value: "Flux1DevDepthLoRA",
          label: t("juno.input.engine.Flux1DevDepthLoRA"),
          description: t("juno.input.engine.Flux1DevDepthLoRADescription"),
          category: ["controlNet"],
        },
        {
          value: "FluxPuLID",
          label: t("juno.input.engine.FluxPuLID"),
          description: t("juno.input.engine.FluxPuLIDDescription"),
          category: [],
        },
        {
          value: "Flux1SchnellRedux",
          label: t("juno.input.engine.Flux1SchnellRedux"),
          description: t("juno.input.engine.Flux1SchnellReduxDescription"),
          category: ["vary"],
        },
        {
          value: "Ghiblify",
          label: t("juno.input.engine.Ghiblify"),
          description: t("juno.input.engine.GhiblifyDescription"),
          category: [],
        },
        {
          value: "IdeogramV3Remix",
          label: t("juno.input.engine.IdeogramV3Remix"),
          description: t("juno.input.engine.IdeogramV3RemixDescription"),
          category: [],
        },
        {
          value: "IdeogramV3ReplaceBackground",
          label: t("juno.input.engine.IdeogramV3ReplaceBackground"),
          description: t("juno.input.engine.IdeogramV3ReplaceBackgroundDescription"),
          category: [],
        },
        {
          value: "InstantCharacter",
          label: t("juno.input.engine.InstantCharacter"),
          description: t("juno.input.engine.InstantCharacterDescription"),
          category: [],
        },

        {
          value: "KolorsVTO",
          label: t("juno.input.engine.KolorsVTO"),
          description: t("juno.input.engine.KolorsVTODescription"),
          category: ["editor"],
        },
        {
          value: "RecraftV3",
          label: t("juno.input.engine.RecraftV3"),
          description: t("juno.input.engine.RecraftV3Description"),
          category: ["vector"],
        },

        {
          value: "SD35",
          label: t("juno.input.engine.SD35"),
          description: t("juno.input.engine.SD35Description"),
          category: [],
        },
        {
          value: "BriaBackgroundReplace",
          label: t("juno.input.engine.BriaBackgroundReplace"),
          description: t("juno.input.engine.BriaBackgroundReplaceDescription"),
          category: [],
        },
        {
          value: "BriaProductShot",
          label: t("juno.input.engine.BriaProductShot"),
          description: t("juno.input.engine.BriaProductShotDescription"),
          category: [],
        },
        {
          value: "IcLightV2",
          label: t("juno.input.engine.IcLightV2"),
          description: t("juno.input.engine.IcLightV2Description"),
          category: [],
        },
      ],
      editor: [
        {
          value: "Flux1ProFill",
          label: t("juno.input.engine.Flux1ProFill"),
          description: t("juno.input.engine.Flux1ProFillDescription"),
          category: ["pro", "editor", "brush"],
        },
        {
          value: "Flux1ProFillJuno",
          label: t("juno.input.engine.Flux1ProFillJuno"),
          description: t("juno.input.engine.Flux1ProFillJunoDescription"),
          category: ["pro", "editor", "juno", "brush"],
        },
        {
          value: "Flux1DevFill",
          label: t("juno.input.engine.Flux1DevFill"),
          description: t("juno.input.engine.Flux1DevFillDescription"),
          category: ["editor", "juno", "brush"],
        },
        {
          value: "IdeogramV3Edit",
          label: t("juno.input.engine.IdeogramV3Edit"),
          description: t("juno.input.engine.IdeogramV3EditDescription"),
          category: ["editor", "brush"],
        },
      ],
      upscale: [
        {
          value: "AuraSR",
          label: t("juno.input.engine.AuraSR"),
          description: t("juno.input.engine.AuraSRDescription"),
          category: [],
        },
        {
          value: "ClarityUpscaler",
          label: t("juno.input.engine.ClarityUpscaler"),
          description: t("juno.input.engine.ClarityUpscalerDescription"),
          category: [],
        },
        {
          value: "CreativeUpscaler",
          label: t("juno.input.engine.CreativeUpscaler"),
          description: t("juno.input.engine.CreativeUpscalerDescription"),
          category: [],
        },
        {
          value: "RecraftCrispUpscaler",
          label: t("juno.input.engine.RecraftCrispUpscaler"),
          description: t("juno.input.engine.RecraftCrispUpscalerDescription"),
          category: [],
        },
        // 400エラーが出るので一時的にコメントアウト
        // {
        //   value: "RecraftCreativeUpscaler",
        //   label: t("juno.input.engine.RecraftCreativeUpscaler"),
        //   description: t("juno.input.engine.RecraftCreativeUpscalerDescription"),
        //   category: [],
        // },
      ],
      context: [
        {
          value: "GPTImage1",
          label: t("juno.input.engine.GPTImage1"),
          description: t("juno.input.engine.GPTImage1Description"),
          category: ["pro"],
        },
        {
          value: "FluxProKontextMax",
          label: t("juno.input.engine.FluxProKontextMax"),
          description: t("juno.input.engine.FluxProKontextMaxDescription"),
          category: ["pro"],
        },
        {
          value: "FluxProKontext",
          label: t("juno.input.engine.FluxProKontext"),
          description: t("juno.input.engine.FluxProKontextDescription"),
          category: [],
        },
        {
          value: "GeminiFlashEdit",
          label: t("juno.input.engine.GeminiFlashEdit"),
          description: t("juno.input.engine.GeminiFlashEditDescription"),
          category: ["editor"],
        },
      ],
    };
  }, [t]);

  const getVideoModels = React.useMemo(() => {
    return [
      {
        value: "HEDRA",
        label: t("juno.video.ai.hedra.title"),
        description: t("juno.video.ai.hedra.description"),
        disabled: ["t2v", "i2v", "v2v", "upscale"],
      },
      {
        value: "GOOGLE",
        label: t("juno.video.ai.google.title"),
        description: t("juno.video.ai.google.description"),
        disabled: ["v2v", "upscale", "lipsync"],
      },
      {
        value: "HAILUO",
        label: t("juno.video.ai.hailuo.title"),
        description: t("juno.video.ai.hailuo.description"),
        disabled: ["v2v", "upscale", "lipsync"],
      },
      {
        value: "HAIPER",
        label: t("juno.video.ai.haiper.title"),
        description: t("juno.video.ai.haiper.description"),
        disabled: ["v2v", "upscale", "lipsync"],
      },
      {
        value: "KLING",
        label: t("juno.video.ai.kling.title"),
        description: t("juno.video.ai.kling.description"),
        disabled: ["v2v", "upscale", "lipsync"],
      },
      {
        value: "FRAMEPACK",
        label: t("juno.video.ai.framepack.title"),
        description: t("juno.video.ai.framepack.description"),
        disabled: ["t2v", "v2v", "upscale", "lipsync"],
      },
      {
        value: "LUMA",
        label: t("juno.video.ai.luma.title"),
        description: t("juno.video.ai.luma.description"),
        disabled: ["upscale", "lipsync"],
      },
      {
        value: "PIKA",
        label: t("juno.video.ai.pika.title"),
        description: t("juno.video.ai.pika.description"),
        disabled: ["upscale", "lipsync"],
      },
      {
        value: "PIXVERSE",
        label: t("juno.video.ai.pixverse.title"),
        description: t("juno.video.ai.pixverse.description"),
        disabled: ["v2v", "upscale", "lipsync"],
      },
      {
        value: "RUNWAY",
        label: t("juno.video.ai.runway.title"),
        description: t("juno.video.ai.runway.description"),
        disabled: ["t2v", "v2v", "upscale", "lipsync"],
      },
      {
        value: "VIDU",
        label: t("juno.video.ai.vidu.title"),
        description: t("juno.video.ai.vidu.description"),
        disabled: ["v2v", "upscale", "lipsync"],
      },
      {
        value: "WAN",
        label: t("juno.video.ai.wan.title"),
        description: t("juno.video.ai.wan.description"),
        disabled: ["v2v", "upscale", "lipsync"],
      },
      {
        value: "BYTEDANCE",
        label: t("juno.video.ai.bytedance.title"),
        description: t("juno.video.ai.bytedance.description"),
        disabled: ["upscale", "v2v"],
      },
      {
        value: "SYNC",
        label: t("juno.video.ai.sync.title"),
        description: t("juno.video.ai.sync.description"),
        disabled: ["t2v", "i2v", "upscale", "v2v"],
      },
      {
        value: "TOPAZLABS",
        label: t("juno.video.ai.topazlabs.title"),
        description: t("juno.video.ai.topazlabs.description"),
        disabled: ["t2v", "i2v", "lipsync", "v2v"],
      },
      {
        value: "REALESRGAN",
        label: t("juno.video.ai.realesrgan.title"),
        description: t("juno.video.ai.realesrgan.description"),
        disabled: ["t2v", "i2v", "lipsync", "v2v"],
      },
    ];
  }, [t]);

  const getSoundModels = React.useMemo(() => {
    return [
      {
        value: "ACE_STEP",
        label: t("juno.sound.ai.ace_step.title"),
        description: t("juno.sound.ai.ace_step.description"),
        category: ["music", "lyric"],
      },
      {
        value: "ACE_STEP_PROMPT_TO_AUDIO",
        label: t("juno.sound.ai.ace_step_prompt_to_audio.title"),
        description: t("juno.sound.ai.ace_step_prompt_to_audio.description"),
        category: ["music"],
      },
      {
        value: "CASSETTEAI",
        label: t("juno.sound.ai.cassetteai.title"),
        description: t("juno.sound.ai.cassetteai.description"),
        category: ["music"],
      },
      {
        value: "CASSETTEAI_SFX",
        label: t("juno.sound.ai.cassetteai_sfx.title"),
        description: t("juno.sound.ai.cassetteai_sfx.description"),
        category: ["sfx"],
      },
      {
        value: "DIFFRHYTHM",
        label: t("juno.sound.ai.diffrhythm.title"),
        description: t("juno.sound.ai.diffrhythm.description"),
        category: ["music", "reference"],
      },
      {
        value: "ELEVENLABS",
        label: t("juno.sound.ai.elevenlabs.title"),
        description: t("juno.sound.ai.elevenlabs.description"),
        category: ["sfx"],
      },
      {
        value: "LYRIA2",
        label: t("juno.sound.ai.lyria2.title"),
        description: t("juno.sound.ai.lyria2.description"),
        category: ["music"],
      },
      {
        value: "MINIMAX",
        label: t("juno.sound.ai.minimax.title"),
        description: t("juno.sound.ai.minimax.description"),
        category: ["music", "lyric", "reference"],
      },
      {
        value: "MMAUDIO",
        label: t("juno.sound.ai.mmaudio.title"),
        description: t("juno.sound.ai.mmaudio.description"),
        category: ["sfx", "music"],
      },
      // {
      //   value: "ACE_STEP_AUDIO_TO_AUDIO",
      //   label: t("juno.sound.ai.ace_step_audio_to_audio.title"),
      //   description: t("juno.sound.ai.ace_step_audio_to_audio.description"),
      //   category: ["music", "reference"],
      // },
    ];
  }, [t]);

  const getSoundModelParams = (aiEngine: string) => {
    const params: string[] = [];

    // 全モデル共通のパラメータ
    params.push("prompt", "public");

    // モデル固有のパラメータ
    switch (aiEngine) {
      case "CASSETTEAI":
      case "CASSETTEAI_SFX":
        params.push("duration");
        break;
      case "DIFFRHYTHM":
        params.push("audioUpload", "duration", "guidanceScale", "step"); //"lyric" English only,
        break;
      case "MINIMAX":
        params.push("audioUpload", "lyric");
        break;
      case "ELEVENLABS":
        params.push("duration", "guidanceScale");
        break;
      case "MMAUDIO":
        // 効果音生成モデルの場合の追加パラメータ
        params.push("duration", "guidanceScale", "step", "negativePrompt");
        break;
      case "ACE_STEP_AUDIO_TO_AUDIO":
        // audio-to-audioモデルの場合の追加パラメータ
        params.push("audioUpload", "tags", "guidanceScale");
        break;
      case "ACE_STEP":
        // ace-stepモデルの場合の追加パラメータ
        params.push("tags", "duration", "lyric", "guidanceScale");
        break;
      case "ACE_STEP_PROMPT_TO_AUDIO":
        // prompt-to-audioモデルの場合の追加パラメータ
        params.push("duration", "instrumental", "guidanceScale");
        break;
      case "LYRIA2":
        // Lyria2モデルの場合の追加パラメータ
        params.push("negativePrompt");
        break;
      default:
        break;
    }

    return params;
  };

  return {
    getModelParams,
    getImageModels,
    getVideoModels,
    getSoundModels,
    getSoundModelParams,
  };
};
